import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Button, Alert, ConfigProvider } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  fetchUserProfile,
  updateUserProfile,
  fetchMerchantsAndManagers,
  setEditingSection,
  setShowMerchantsAndManagers,
  filterMerchantsAndManagers,
  setSettingsSaved,
} from '../../redux/Settings';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  GridContainer,
} from './Styles';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { notification } from '../../commonComponents/Notification/Notification';
import ProfileSection from './components/ProfileSection';
import ProfileEditForm from './components/ProfileEditForm';
import PersonalInformationSection from './components/PersonalInformationSection';
import AddressSection from './components/AddressSection';
import TaxConfigurationSection from './components/TaxConfigurationSection';
import PreferencesSection from './components/PreferencesSection';
import TeamManagementSection from './components/TeamManagementSection';
import type { SettingsProps } from './types';

const Settings: React.FC<SettingsProps> = ({ role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );

  // Redux state
  const {
    user,
    filteredMerchantsAndManagers,
    loading,
    error,
    editingSection,
    showMerchantsAndManagers,
    settingsSaved,
  } = useSelector((state: RootState) => state.settings);

  // Local state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    address: '',
    email: '',
    phone: '',
    tax: 0,
    loyaltyEnabled: false,
    loyaltyRedeemRatio: '',
    loyaltySpendRatio: '',
    receiveNotifications: false,
    autoRenewSubscription: false,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Fetch user profile on mount
  useEffect(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  // Update form data when user profile is loaded
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        tax:
          user.tax !== undefined && user.tax !== null && !isNaN(user.tax)
            ? user.tax * 100
            : 0,
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
      });
    }
  }, [user]);

  // Fetch merchants/managers when section is shown
  useEffect(() => {
    if (showMerchantsAndManagers) {
      dispatch(fetchMerchantsAndManagers({ role }));
    }
  }, [dispatch, role, showMerchantsAndManagers]);

  // Handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    },
    []
  );

  const handleNumberChange = useCallback((name: string, value: number | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value || 0,
    }));
  }, []);

  const handleSwitchChange = useCallback((field: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  }, []);

  const handleEdit = useCallback(
    (section: string) => {
      dispatch(setEditingSection(section));
      dispatch(setSettingsSaved(false));
    },
    [dispatch]
  );

  const handleSave = useCallback(async () => {
    if (!formData.name?.trim() || !formData.username?.trim() || !formData.email?.trim()) {
      notification.error('Name, username, and email are required fields and cannot be empty.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      notification.error('Please enter a valid email address.');
      return;
    }

    if (!user) return;

    try {
      const updatedData = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        address: formData.address.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        loyaltyEnabled: formData.loyaltyEnabled,
        loyaltyRedeemRatio: formData.loyaltyRedeemRatio,
        loyaltySpendRatio: formData.loyaltySpendRatio,
        tax: formData.tax / 100,
        receiveNotifications: formData.receiveNotifications,
        autoRenewSubscription: formData.autoRenewSubscription,
      };

      await dispatch(
        updateUserProfile({
          role,
          userId: user.id,
          data: updatedData,
          logoFile: logoFile || undefined,
          removeLogo: removeLogo,
        })
      ).unwrap();

      // Clear logo file and remove flag after successful upload
      setLogoFile(null);
      setRemoveLogo(false);

      notification.success('Settings updated successfully!');
    } catch (error: any) {
      notification.error(error || 'Failed to update settings.');
    }
  }, [dispatch, formData, role, user, logoFile, removeLogo]);

  const handleCancel = useCallback(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        tax:
          user.tax !== undefined && user.tax !== null && !isNaN(user.tax)
            ? user.tax * 100
            : 0,
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
      });
    }
    setLogoFile(null);
    setRemoveLogo(false);
    dispatch(setEditingSection(null));
  }, [dispatch, user]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchUserProfile({ role }));
    if (showMerchantsAndManagers) {
      dispatch(fetchMerchantsAndManagers({ role }));
    }
  }, [dispatch, role, showMerchantsAndManagers]);

  const handleMoreDetailsClick = useCallback(() => {
    if (role === 'merchant') {
      navigate('/merchantmanager');
    } else if (role === 'admin') {
      navigate('/merchants');
    }
  }, [navigate, role]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      dispatch(filterMerchantsAndManagers(value));
    },
    [dispatch]
  );

  const toggleMerchantsAndManagers = useCallback(() => {
    dispatch(setShowMerchantsAndManagers(!showMerchantsAndManagers));
  }, [dispatch, showMerchantsAndManagers]);

  if (error && !user && !loading.profile) {
    return (
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Container>
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={handleRefresh}>
                  Try Again
                </Button>
              }
            />
          </Container>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>Account Settings</Title>
              <Subtitle>Manage your profile and preferences</Subtitle>
            </div>
          </HeaderSection>

          <ProfileSection
            user={user}
            role={role}
            editingSection={editingSection}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('profile')}
          />

          {editingSection === 'profile' && user && (
            <ProfileEditForm
              formData={{
                name: formData.name,
                username: formData.username,
                address: formData.address,
              }}
              logo={(user as any).logo || (user as any).merchant?.logo || null}
              loading={loading.update}
              onInputChange={handleInputChange}
              onLogoChange={(file) => {
                setLogoFile(file);
                setRemoveLogo(file === null);
              }}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}

          <GridContainer>
            <PersonalInformationSection
              user={user}
              editingSection={editingSection}
              formData={{
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('personal')}
              onInputChange={handleInputChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <AddressSection
              user={user}
              editingSection={editingSection}
              formData={{ address: formData.address }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('address')}
              onInputChange={handleInputChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <TaxConfigurationSection
              user={user}
              editingSection={editingSection}
              formData={{ tax: formData.tax }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('tax')}
              onNumberChange={handleNumberChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <PreferencesSection
              editingSection={editingSection}
              formData={{
                receiveNotifications: formData.receiveNotifications,
                autoRenewSubscription: formData.autoRenewSubscription,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('additional')}
              onSwitchChange={handleSwitchChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </GridContainer>

          <TeamManagementSection
            role={role}
            showMerchantsAndManagers={showMerchantsAndManagers}
            filteredMerchantsAndManagers={filteredMerchantsAndManagers}
            loading={loading.merchantsAndManagers}
            searchValue={searchValue}
            onToggle={toggleMerchantsAndManagers}
            onSearch={handleSearch}
            onMoreDetailsClick={handleMoreDetailsClick}
          />

          {settingsSaved && !editingSection && (
            <Alert
              message="Settings Saved!"
              description="Your account settings have been updated successfully."
              type="success"
              showIcon
              closable
              style={{ marginTop: 24 }}
            />
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Settings);
